import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";

import IconButton from "@mui/material/IconButton";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import LinearProgress from "@mui/material/LinearProgress";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import Tooltip from "@mui/material/Tooltip";
import SettingsIcon from "@mui/icons-material/Settings";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import TabletIcon from '@mui/icons-material/Tablet';
import PrintIcon from '@mui/icons-material/Print';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

import { NavLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaCreditCard } from 'react-icons/fa';
import { IoPrint } from 'react-icons/io5';
import { RiBankLine } from "react-icons/ri";
import { FaUber } from 'react-icons/fa';
import { 
  useUberIntegrationConfig, 
  useDisconnectUberIntegration, 
  useUberAuthUrl
} from 'apis/uberAPIs';
import { updateSelectedRestaurant } from 'store/actions/restaurant';


const useStyles = makeStyles((theme) => ({
  root: {},
  row: {
    height: "42px",
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  spacer: {
    flexGrow: 1,
  },
  importButton: {
    marginRight: theme.spacing(1),
  },
  exportButton: {
    marginRight: theme.spacing(1),
  },
  searchInput: {
    marginRight: theme.spacing(1),
  },
}));

const RestaurantToolbar = (props) => {
  const { className, proxy, onOpenDeliverySettings, ...rest } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  
  const [uberDialogOpen, setUberDialogOpen] = useState(false);
  const restaurant = useSelector(
    (state) => state.restaurant.selectedRestaurant
  );
  const user = useSelector((state) => state.account.user);

  // Always call hooks first, before any conditional returns
  // Use a stable boolean to avoid conditional hook calls
  const hasUberStoreId = Boolean(restaurant?.uberEatsStoreId);
  const { 
    data: uberConfig, 
    isLoading: configLoading,
    refetch: refetchConfig 
  } = useUberIntegrationConfig(hasUberStoreId);

  const disconnectMutation = useDisconnectUberIntegration();
  const authUrlMutation = useUberAuthUrl();

  // Early return after all hooks are called
  if (!restaurant) {
    return null;
  }

  // Handlers
  const handleUberIntegration = async () => {
    if (restaurant.uberEatsStoreId) {
      // If restaurant already has uberEatsStoreId, show integration details
      if (uberConfig) {
        // console.log('uberConfig', uberConfig);
        setUberDialogOpen(true);
      } else {
        // Refetch config if not loaded yet
        refetchConfig();
        setUberDialogOpen(true);
      }
    } else {
      // If no uberEatsStoreId, initiate OAuth flow
      try {
        const response = await authUrlMutation.mutateAsync(restaurant.urlIdentifier);
        if (response?.authUrl) {
          window.open(response.authUrl, '_blank', 'width=600,height=800');
        }
      } catch (error) {
        console.error('Error getting Uber auth URL:', error);
      }
    }
  };

  const handleDisconnectUber = async () => {
    try {
      await disconnectMutation.mutateAsync();
      
      // Clear uberEatsStoreId from restaurant state after successful disconnection
      if (restaurant?.uberEatsStoreId) {
        dispatch(updateSelectedRestaurant({ 
          ...restaurant, 
          uberEatsStoreId: null 
        }));
      }
      
      setUberDialogOpen(false);
      // console.log('Uber integration disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting Uber integration:', error);
    }
  };

  const handleCloseUberDialog = () => {
    setUberDialogOpen(false);
  };


  const isLoading = configLoading || disconnectMutation.isLoading || authUrlMutation.isLoading;

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      {isLoading ? <LinearProgress /> : null}
      <div className={classes.row}>
        <span className={classes.spacer} />

         {/* <Button variant="filled" style={{ backgroundColor: green[700] }} onClick={openAdyenSettings}>Adyen</Button> */}

        <Tooltip title="Uber Eats Integration">
          <IconButton
            aria-label="uber eats integration"
            onClick={handleUberIntegration}
            sx={{ color: '#000000' }}
          >
            <FaUber />
          </IconButton>
        </Tooltip>
        {/* <NavLink to="/grantOffers" >
          <Tooltip title="Adyen Capital">
            <IconButton aria-label="Adyen Capital">
            <RiBankLine />
              
            </IconButton>
          </Tooltip>
        </NavLink> */}
    

        <NavLink to="/subscriptions" >
          <Tooltip title="Subscriptions">
            <IconButton aria-label="service subscriptions" sx={{ color: '#9c27b0' }}>
              <SubscriptionsIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/paymentTypes" >
          <Tooltip title="Payment types">
            <IconButton aria-label="Payment types" sx={{ color: '#2e7d32' }}>
              <FaCreditCard />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/surchargeSettings" >
          <Tooltip title="Surcharge Settings">
            <IconButton aria-label="Surcharges" sx={{ color: '#1976d2' }}>
              <CreditCardIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/posSettings" className={classes.textLink}>
          <Tooltip title="POS Settings">
            <IconButton aria-label="POS settings" sx={{ color: '#0288d1' }}>
              <TabletIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/posPrinterSettings" className={classes.textLink}>
          <Tooltip title="POS Printers Settings">
            <IconButton aria-label="POS Printers settings" sx={{ color: '#455a64' }}>
              <IoPrint />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/posPrintSettings" className={classes.textLink}>
          <Tooltip title="POS Printing Settings">
            <IconButton aria-label="POS Printing settings" sx={{ color: '#546e7a' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/restaurantSettings" className={classes.textLink}>
          <Tooltip title="Online Settings">
            <IconButton aria-label="online settings" sx={{ color: '#757575' }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <Tooltip title="Delivery Settings">
          <IconButton
            aria-label="delivery settings"
            onClick={onOpenDeliverySettings}
            sx={{ color: '#f57c00' }}
          >
            <LocalShippingIcon />
          </IconButton>
        </Tooltip>

        <NavLink to="/serviceBellTypes" className={classes.textLink}>
          <Tooltip title="Service Bell Settings">
            <IconButton aria-label="service bell" sx={{ color: '#fbc02d' }}>
              <RoomServiceIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to="/openHours" className={classes.textLink}>
          <Tooltip title="Open Hours Settings">
            <IconButton aria-label="open hours" sx={{ color: '#00897b' }}>
              <AccessTimeIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink to={"/user"} className={classes.textLink}>
          <Tooltip title="Restaurant Managers">
            <IconButton aria-label="add restaurant manager" sx={{ color: '#5c6bc0' }}>
              <PersonAddIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        <NavLink
          to={`/qrcodes/${props.restaurant.urlIdentifier}`}
          className={classes.textLink}
        >
          <Tooltip title="QR Code">
            <IconButton aria-label="qr code" sx={{ color: '#7b1fa2' }}>
              <AssignmentReturnedIcon />
            </IconButton>
          </Tooltip>
        </NavLink>

        {user && (user.role === "admin" || user.role === "menuAdmin") && (
          <NavLink to="/qrcode-generator" className={classes.textLink}>
            <Tooltip title="QR Code Generator">
              <IconButton aria-label="qr code generator" sx={{ color: '#00695c' }}>
                <QrCode2Icon />
              </IconButton>
            </Tooltip>
          </NavLink>
        )}
       
      </div>

      {/* Uber Integration Config Dialog */}
      <Dialog 
        open={uberDialogOpen} 
        onClose={handleCloseUberDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
            <FaUber size={24} />
            <Typography variant="h6">Uber Eats Integration Configuration</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {uberConfig && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Store Name</Typography>
                <Typography variant="body1">{uberConfig.uberStoreName || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Local Restaurant ID</Typography>
                <Typography variant="body1">{uberConfig.localRestaurantId || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Store ID</Typography>
                <Typography variant="body1" style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                  {uberConfig.store_id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Partner Store ID</Typography>
                <Typography variant="body1" style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                  {uberConfig.partner_store_id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Connection Status</Typography>
                <Chip 
                  label={uberConfig.isConnected ? 'Connected' : 'Disconnected'} 
                  color={uberConfig.isConnected ? 'primary' : 'default'}
                  variant={uberConfig.isConnected ? 'default' : 'outlined'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Online Status</Typography>
                <Chip 
                  label={uberConfig.online_status} 
                  color={uberConfig.online_status === 'online' ? 'primary' : 'default'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">POS Integration</Typography>
                <Chip 
                  label={uberConfig.pos_integration_enabled ? 'Enabled' : 'Disabled'} 
                  color={uberConfig.pos_integration_enabled ? 'primary' : 'default'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Auto Accept</Typography>
                <Chip 
                  label={uberConfig.auto_accept_enabled ? 'Enabled' : 'Disabled'} 
                  color={uberConfig.auto_accept_enabled ? 'primary' : 'default'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Order Release</Typography>
                <Chip 
                  label={uberConfig.order_release_enabled ? 'Enabled' : 'Disabled'} 
                  color={uberConfig.order_release_enabled ? 'primary' : 'default'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Integration Enabled</Typography>
                <Chip 
                  label={uberConfig.integration_enabled ? 'Enabled' : 'Disabled'} 
                  color={uberConfig.integration_enabled ? 'primary' : 'default'}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Integrator Details</Typography>
                <Typography variant="body2">Store ID: {uberConfig.integrator_store_id}</Typography>
                <Typography variant="body2">Brand ID: {uberConfig.integrator_brand_id}</Typography>
              </Grid>

              {uberConfig.webhooks_config && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Webhooks Configuration</Typography>
                  <Box mt={1}>
                    <Typography variant="body2">Version: {uberConfig.webhooks_config.webhooks_version}</Typography>
                    <Typography variant="body2">
                      Schedule Order Webhooks: {uberConfig.webhooks_config.schedule_order_webhooks?.is_enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                    <Typography variant="body2">
                      Order Release Webhooks: {uberConfig.webhooks_config.order_release_webhooks?.is_enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                    <Typography variant="body2">
                      Delivery Status Webhooks: {uberConfig.webhooks_config.delivery_status_webhooks?.is_enabled ? 'Enabled' : 'Disabled'}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUberDialog}>
            Close
          </Button>
          <Button 
            onClick={handleDisconnectUber} 
            color="error" 
            variant="outlined"
            disabled={disconnectMutation.isLoading}
          >
            {disconnectMutation.isLoading ? 'Disconnecting...' : 'Disconnect Store'}
          </Button>
        </DialogActions>
      </Dialog>


    </div>
  );
};

RestaurantToolbar.propTypes = {
  className: PropTypes.string,
  onOpenDeliverySettings: PropTypes.func,
};

export default RestaurantToolbar;
